import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, PackageCheck, Plus, X, Store, RefreshCw, Filter,
  Eye, AlertCircle, DollarSign, User, Truck, CheckCircle, Save
} from 'lucide-react';
import api, { API_URL } from '../../services/api';

const Depots = () => {
  const navigate = useNavigate();
  const [depots, setDepots] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [clients, setClients] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filtres
  const [filtreBoutique, setFiltreBoutique] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('actif');
  const [filtreModalite, setFiltreModalite] = useState('');

  // Modal nouveau depot
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deposantType, setDeposantType] = useState('client'); // client | fournisseur | libre
  const [form, setForm] = useState({
    date_depot: new Date().toISOString().split('T')[0],
    client_id: '',
    fournisseur_id: '',
    deposant_nom: '',
    deposant_telephone: '',
    boutique_id: '',
    modalite: 'DEPOT_VENTE',
    montant_achat_total: '',
    date_paiement_achat: new Date().toISOString().split('T')[0],
    mode_paiement_achat: 'especes',
    reference_paiement: '',
    notes: ''
  });

  // Bijoux a regler
  const [showAReglerModal, setShowAReglerModal] = useState(false);
  const [bijouxARegler, setBijouxARegler] = useState({ bijoux: [], total_a_regler: 0, nb: 0 });

  // Modal reglement
  const [reglementBijou, setReglementBijou] = useState(null);
  const [reglementForm, setReglementForm] = useState({ montant: '', mode_paiement: 'especes', date_paiement: new Date().toISOString().split('T')[0], reference: '' });

  const fmt = (m) => parseFloat(m||0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' DH';
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  // ===== API =====
  const loadAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtreBoutique) params.append('boutique_id', filtreBoutique);
      if (filtreStatut) params.append('statut', filtreStatut);
      if (filtreModalite) params.append('modalite', filtreModalite);

      const [depotsR, boutiquesR, clientsR, fournR, statsR] = await Promise.all([
        api.get(`${API_URL}/depots?${params.toString()}`),
        api.get(`${API_URL}/boutiques`),
        api.get(`${API_URL}/clients`),
        api.get(`${API_URL}/fournisseurs`),
        api.get(`${API_URL}/depots/stats`),
      ]);
      setDepots(depotsR.data || []);
      setBoutiques(Array.isArray(boutiquesR.data) ? boutiquesR.data : []);
      setClients(Array.isArray(clientsR.data) ? clientsR.data : (clientsR.data?.clients || []));
      setFournisseurs(Array.isArray(fournR.data) ? fournR.data : (fournR.data?.fournisseurs || []));
      setStats(statsR.data);
    } catch (err) { setError('Erreur chargement'); console.error(err); }
    finally { setLoading(false); }
  };

  const loadARegler = async () => {
    try {
      const r = await api.get(`${API_URL}/depots/a-regler`);
      setBijouxARegler(r.data || { bijoux: [], total_a_regler: 0, nb: 0 });
      setShowAReglerModal(true);
    } catch (err) { setError('Erreur chargement'); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  // ===== Actions =====
  const handleSubmit = async () => {
    if (!form.boutique_id) { setError('Boutique requise'); return; }
    if (deposantType === 'client' && !form.client_id) { setError('Client requis'); return; }
    if (deposantType === 'fournisseur' && !form.fournisseur_id) { setError('Fournisseur requis'); return; }
    if (deposantType === 'libre' && !form.deposant_nom.trim()) { setError('Nom du deposant requis'); return; }
    if (form.modalite === 'ACHAT_DIRECT' && (!form.montant_achat_total || parseFloat(form.montant_achat_total) <= 0)) {
      setError('Montant d\'achat requis pour ACHAT_DIRECT'); return;
    }

    setSubmitting(true); setError('');
    try {
      const payload = {
        date_depot: form.date_depot,
        boutique_id: parseInt(form.boutique_id),
        modalite: form.modalite,
        client_id: deposantType === 'client' ? parseInt(form.client_id) : null,
        fournisseur_id: deposantType === 'fournisseur' ? parseInt(form.fournisseur_id) : null,
        deposant_nom: deposantType === 'libre' ? form.deposant_nom.trim() : null,
        deposant_telephone: deposantType === 'libre' ? form.deposant_telephone : null,
        montant_achat_total: form.modalite === 'ACHAT_DIRECT' ? parseFloat(form.montant_achat_total) : 0,
        date_paiement_achat: form.modalite === 'ACHAT_DIRECT' ? form.date_paiement_achat : null,
        mode_paiement_achat: form.modalite === 'ACHAT_DIRECT' ? form.mode_paiement_achat : null,
        reference_paiement: form.reference_paiement,
        notes: form.notes,
      };
      const r = await api.post(`${API_URL}/depots`, payload);
      setSuccess(`Depot ${r.data.numero_depot} cree`);
      setShowForm(false);
      // Reset form
      setForm({
        date_depot: new Date().toISOString().split('T')[0],
        client_id: '', fournisseur_id: '', deposant_nom: '', deposant_telephone: '',
        boutique_id: '', modalite: 'DEPOT_VENTE',
        montant_achat_total: '', date_paiement_achat: new Date().toISOString().split('T')[0],
        mode_paiement_achat: 'especes', reference_paiement: '', notes: ''
      });
      await loadAll();
      // Rediriger vers la fiche du nouveau depot pour ajouter des bijoux
      setTimeout(() => navigate(`/depots/${r.data.id}`), 800);
    } catch (err) { setError(err.response?.data?.error || 'Erreur creation'); }
    finally { setSubmitting(false); }
  };

  const ouvrirReglement = (bijou) => {
    setReglementBijou(bijou);
    setReglementForm({
      montant: bijou.reste_a_payer || bijou.montant_du_deposant || 0,
      mode_paiement: 'especes',
      date_paiement: new Date().toISOString().split('T')[0],
      reference: ''
    });
  };

  const handleReglement = async () => {
    if (!reglementBijou) return;
    if (!reglementForm.montant || parseFloat(reglementForm.montant) <= 0) { setError('Montant requis'); return; }
    setSubmitting(true);
    try {
      await api.post(`${API_URL}/depots/bijoux/${reglementBijou.id}/regler-deposant`, {
        montant: parseFloat(reglementForm.montant),
        mode_paiement: reglementForm.mode_paiement,
        date_paiement: reglementForm.date_paiement,
        reference: reglementForm.reference,
      });
      setSuccess('Deposant regle');
      setReglementBijou(null);
      await loadARegler();
      await loadAll();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Erreur reglement'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>;

  return (
    <div className="min-h-screen bg-gray-100 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 to-pink-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Achat & Dépôts</h1>
              <p className="text-purple-200 text-sm">Bijoux deposes par clients ou fournisseurs</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-white text-purple-700 px-4 py-2 rounded-lg font-medium hover:bg-purple-50">
                <Plus className="h-4 w-4" /><span>Nouveau depot</span>
              </button>
              <button onClick={loadARegler} className="flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-amber-600">
                <DollarSign className="h-4 w-4" /><span>A regler</span>
                {stats?.montant_a_regler > 0 && <span className="bg-white text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">{fmt(stats.montant_a_regler)}</span>}
              </button>
              <button onClick={loadAll} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg"><RefreshCw className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between"><span className="flex items-center gap-2"><AlertCircle className="h-4 w-4" />{error}</span><button onClick={() => setError('')}><X className="h-4 w-4" /></button></div>}
        {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2"><CheckCircle className="h-4 w-4" />{success}</div>}

        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-sm border p-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Boutique</label>
            <select value={filtreBoutique} onChange={e => setFiltreBoutique(e.target.value)} className="px-3 py-2 border rounded-lg bg-white text-sm">
              <option value="">Toutes</option>
              {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Statut</label>
            <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)} className="px-3 py-2 border rounded-lg bg-white text-sm">
              <option value="">Tous</option>
              <option value="actif">Actif</option>
              <option value="termine">Termine</option>
              <option value="annule">Annule</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Modalite</label>
            <select value={filtreModalite} onChange={e => setFiltreModalite(e.target.value)} className="px-3 py-2 border rounded-lg bg-white text-sm">
              <option value="">Toutes</option>
              <option value="DEPOT_VENTE">Depot-vente</option>
              <option value="ACHAT_DIRECT">Achat direct</option>
            </select>
          </div>
          <button onClick={loadAll} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
            <Filter className="h-4 w-4" />
          </button>
        </div>

        {/* KPIs */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <p className="text-xs text-gray-500">Depots actifs</p>
              <p className="text-2xl font-black text-purple-700">{stats.nb_depots_actifs}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <p className="text-xs text-gray-500">Bijoux en stock</p>
              <p className="text-2xl font-black text-blue-700">{stats.nb_bijoux_en_stock}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <p className="text-xs text-gray-500">A verser aux deposants</p>
              <p className="text-xl font-black text-amber-700">{fmt(stats.montant_a_regler)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <p className="text-xs text-gray-500">Achats directs (mois)</p>
              <p className="text-xl font-black text-green-700">{fmt(stats.achats_directs_mois?.total)}</p>
              <p className="text-xs text-gray-400">{stats.achats_directs_mois?.nb} achats</p>
            </div>
          </div>
        )}

        {/* Liste depots */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b">
            <h2 className="font-bold text-gray-800 flex items-center gap-2"><Package className="h-5 w-5 text-purple-600" />DEPOTS ({depots.length})</h2>
          </div>

          {depots.length === 0 ? (
            <div className="text-center py-12">
              <PackageCheck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun depot</p>
              <button onClick={() => setShowForm(true)} className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">Creer le premier depot</button>
            </div>
          ) : (
            <>
              {/* Mobile */}
              <div className="md:hidden divide-y">
                {depots.map(d => (
                  <div key={d.id} className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/depots/${d.id}`)}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-bold text-gray-900">{d.numero_depot}</p>
                        <p className="text-xs text-gray-500">{fmtDate(d.date_depot)} - {d.boutique_nom}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${d.modalite === 'DEPOT_VENTE' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                        {d.modalite === 'DEPOT_VENTE' ? 'Depot-vente' : 'Achat direct'}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-700">{d.deposant_label}</p>
                    <div className="flex gap-2 mt-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700">{d.nb_bijoux} bijoux</span>
                      {d.nb_vendus > 0 && <span className="px-2 py-0.5 rounded bg-green-50 text-green-700">{d.nb_vendus} vendus</span>}
                      {parseFloat(d.montant_a_regler) > 0 && <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 font-bold">A regler: {fmt(d.montant_a_regler)}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">N°</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Deposant</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Boutique</th>
                      <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Modalite</th>
                      <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Bijoux</th>
                      <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">A regler</th>
                      <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Statut</th>
                      <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {depots.map(d => (
                      <tr key={d.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/depots/${d.id}`)}>
                        <td className="px-4 py-3 font-mono font-bold text-purple-700">{d.numero_depot}</td>
                        <td className="px-4 py-3 text-gray-600">{fmtDate(d.date_depot)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {d.deposant_type === 'client' && <User className="h-4 w-4 text-blue-500" />}
                            {d.deposant_type === 'fournisseur' && <Truck className="h-4 w-4 text-orange-500" />}
                            <span className="font-medium">{d.deposant_label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{d.boutique_nom}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${d.modalite === 'DEPOT_VENTE' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                            {d.modalite === 'DEPOT_VENTE' ? 'Depot-vente' : 'Achat direct'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-bold">{d.nb_bijoux}</span>
                          {d.nb_vendus > 0 && <span className="text-xs text-green-600 ml-1">({d.nb_vendus} vendus)</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {parseFloat(d.montant_a_regler) > 0 ?
                            <span className="font-bold text-amber-700">{fmt(d.montant_a_regler)}</span> :
                            <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            d.statut === 'actif' ? 'bg-green-100 text-green-700' :
                            d.statut === 'termine' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-700'
                          }`}>{d.statut}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button className="text-purple-600 hover:text-purple-800"><Eye className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ===== MODAL NOUVEAU DEPOT ===== */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-lg font-semibold">Nouveau depot</h3>
              <button onClick={() => setShowForm(false)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Type deposant */}
              <div>
                <label className="block text-xs text-gray-500 mb-2">Deposant *</label>
                <div className="flex gap-2 mb-3">
                  {[
                    {key:'client', label:'Client', icon:User, color:'blue'},
                    {key:'fournisseur', label:'Fournisseur', icon:Truck, color:'orange'},
                    {key:'libre', label:'Saisie libre', icon:User, color:'gray'},
                  ].map(t => {
                    const Icon = t.icon;
                    const active = deposantType === t.key;
                    return (
                      <button key={t.key} onClick={() => setDeposantType(t.key)}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ${
                          active ? `bg-${t.color}-100 border-${t.color}-300 text-${t.color}-700` : 'bg-white border-gray-300 text-gray-600'
                        }`}>
                        <Icon className="h-4 w-4" /><span>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
                {deposantType === 'client' && (
                  <select value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">-- Choisir un client --</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.nom} {c.telephone ? `(${c.telephone})` : ''}</option>)}
                  </select>
                )}
                {deposantType === 'fournisseur' && (
                  <select value={form.fournisseur_id} onChange={e => setForm({...form, fournisseur_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">-- Choisir un fournisseur --</option>
                    {fournisseurs.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                  </select>
                )}
                {deposantType === 'libre' && (
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" placeholder="Nom du deposant" value={form.deposant_nom} onChange={e => setForm({...form, deposant_nom: e.target.value})} className="px-3 py-2 border rounded-lg" />
                    <input type="text" placeholder="Telephone" value={form.deposant_telephone} onChange={e => setForm({...form, deposant_telephone: e.target.value})} className="px-3 py-2 border rounded-lg" />
                  </div>
                )}
              </div>

              {/* Boutique + date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Boutique *</label>
                  <select value={form.boutique_id} onChange={e => setForm({...form, boutique_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">-- Choisir --</option>
                    {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date depot</label>
                  <input type="date" value={form.date_depot} onChange={e => setForm({...form, date_depot: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>

              {/* Modalite */}
              <div>
                <label className="block text-xs text-gray-500 mb-2">Modalite *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setForm({...form, modalite: 'DEPOT_VENTE'})}
                    className={`p-3 rounded-lg border-2 text-left ${form.modalite === 'DEPOT_VENTE' ? 'bg-purple-50 border-purple-400' : 'bg-white border-gray-200'}`}>
                    <p className="font-bold text-sm">DEPOT-VENTE</p>
                    <p className="text-xs text-gray-500">Consignation, regle apres vente</p>
                  </button>
                  <button onClick={() => setForm({...form, modalite: 'ACHAT_DIRECT'})}
                    className={`p-3 rounded-lg border-2 text-left ${form.modalite === 'ACHAT_DIRECT' ? 'bg-green-50 border-green-400' : 'bg-white border-gray-200'}`}>
                    <p className="font-bold text-sm">ACHAT DIRECT</p>
                    <p className="text-xs text-gray-500">On achete maintenant, devient stock</p>
                  </button>
                </div>
              </div>

              {/* Si ACHAT_DIRECT : montant + paiement */}
              {form.modalite === 'ACHAT_DIRECT' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-bold text-green-800">Paiement de l'achat</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Montant total (DH) *</label>
                      <input type="text" inputMode="decimal" value={form.montant_achat_total} onChange={e => setForm({...form, montant_achat_total: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-lg font-bold" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Mode paiement</label>
                      <select value={form.mode_paiement_achat} onChange={e => setForm({...form, mode_paiement_achat: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                        <option value="especes">Especes</option>
                        <option value="cheque">Cheque</option>
                        <option value="virement">Virement</option>
                        <option value="carte">Carte</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Date paiement</label>
                      <input type="date" value={form.date_paiement_achat} onChange={e => setForm({...form, date_paiement_achat: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Reference</label>
                      <input type="text" value={form.reference_paiement} onChange={e => setForm({...form, reference_paiement: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="N° cheque..." />
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows="2" placeholder="Notes..." />
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={handleSubmit} disabled={submitting} className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2">
                <Save className="h-4 w-4" /><span>{submitting ? 'Creation...' : 'Creer le depot'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL BIJOUX A REGLER ===== */}
      {showAReglerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Bijoux a regler aux deposants</h3>
                <p className="text-sm text-amber-100">{bijouxARegler.nb} bijoux • Total: {fmt(bijouxARegler.total_a_regler)}</p>
              </div>
              <button onClick={() => setShowAReglerModal(false)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {bijouxARegler.bijoux.length === 0 ? (
                <p className="text-center py-8 text-gray-500">Aucun bijou en attente</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Date vente</th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Bijou</th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Deposant</th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Boutique</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Du</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Verse</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Reste</th>
                        <th className="px-3 py-2 text-center text-xs text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {bijouxARegler.bijoux.map(b => (
                        <tr key={b.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-600">{fmtDate(b.date_vente)}</td>
                          <td className="px-3 py-2">
                            <p className="font-medium text-gray-900">{b.nom}</p>
                            <p className="text-xs text-gray-400">{b.id_produit}</p>
                          </td>
                          <td className="px-3 py-2">{b.deposant_label}</td>
                          <td className="px-3 py-2 text-gray-600">{b.boutique_nom}</td>
                          <td className="px-3 py-2 text-right font-medium">{fmt(b.montant_du_deposant)}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{fmt(b.montant_verse_deposant)}</td>
                          <td className="px-3 py-2 text-right font-bold text-amber-700">{fmt(b.reste_a_payer)}</td>
                          <td className="px-3 py-2 text-center">
                            <button onClick={() => ouvrirReglement(b)} className="px-3 py-1 bg-amber-500 text-white rounded text-xs font-medium hover:bg-amber-600">Regler</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL REGLEMENT ===== */}
      {reglementBijou && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-lg font-semibold">Regler le deposant</h3>
              <button onClick={() => setReglementBijou(null)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-700"><span className="font-bold">Bijou:</span> {reglementBijou.nom} ({reglementBijou.id_produit})</p>
                <p className="text-sm text-gray-700"><span className="font-bold">Deposant:</span> {reglementBijou.deposant_label}</p>
                <p className="text-sm text-gray-700"><span className="font-bold">Reste du:</span> <span className="text-amber-700 font-bold">{fmt(reglementBijou.reste_a_payer)}</span></p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Montant *</label>
                <input type="text" inputMode="decimal" value={reglementForm.montant} onChange={e => setReglementForm({...reglementForm, montant: e.target.value})} onFocus={e => e.target.select()} className="w-full px-3 py-2 border rounded-lg text-lg font-bold" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Mode paiement</label>
                  <select value={reglementForm.mode_paiement} onChange={e => setReglementForm({...reglementForm, mode_paiement: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="especes">Especes</option>
                    <option value="cheque">Cheque</option>
                    <option value="virement">Virement</option>
                    <option value="carte">Carte</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date</label>
                  <input type="date" value={reglementForm.date_paiement} onChange={e => setReglementForm({...reglementForm, date_paiement: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Reference</label>
                <input type="text" value={reglementForm.reference} onChange={e => setReglementForm({...reglementForm, reference: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="N° cheque, ref virement..." />
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => setReglementBijou(null)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={handleReglement} disabled={submitting} className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2">
                <DollarSign className="h-4 w-4" /><span>{submitting ? 'Reglement...' : 'Confirmer'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Depots;
